import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
Button,
Stack,
TextField,
MenuItem,
Chip,
Tooltip,
Pagination
} from "@mui/material";

import { Colors } from "../utils/colors";
import { useNavigate } from "react-router-dom";
import { adminFetch } from "../services/adminFetch";
import { useSelector } from "react-redux";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import AdminPageContainer from "../components/AdminPageContainer";
import {
  adminPageSx,
  contentCardSx,
  filterStackSx,
  mobileActionGroupSx,
  paginationBarSx
} from "../utils/adminResponsive";

const adminFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: 1,
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

export default function Admins(){

const [admins,setAdmins] = useState([])
const [loading,setLoading] = useState(true)
const navigate = useNavigate();

const adminUser = useSelector((state) => state.auth.admin);
const isSuperAdmin = adminUser === "super_admin"? true:false;


const [search,setSearch] = useState("")
const [roleFilter,setRoleFilter] = useState("all")


const [page,setPage] = useState(1)
const rowsPerPage = 5



useEffect(()=>{

const fetchAdmins = async () => {

try{

const data = await adminFetch("/api/user/all/admins", {
  method: "GET"
});

if(data.status){
setAdmins(data.admins.rows || [])
}

}catch(err){
console.log(err)
}

setLoading(false)

}

fetchAdmins()

},[])




const filteredAdmins = admins.filter((admin)=>{

const fullName = `${admin.first_name} ${admin.last_name}`.toLowerCase()

const matchesSearch =
fullName.includes(search.toLowerCase()) ||
admin.email.toLowerCase().includes(search.toLowerCase())

const matchesRole =
roleFilter === "all" || admin.role === roleFilter

return matchesSearch && matchesRole

})




const totalPages = Math.ceil(filteredAdmins.length / rowsPerPage)

const paginatedAdmins = filteredAdmins.slice(
(page - 1) * rowsPerPage,
page * rowsPerPage
)




const getRoleChip = (role) => {

switch(role){
case "super_admin":
return <Chip label="Super Admin" sx={{bgcolor:"#6a1b9a",color:"#fff"}}/>

case "admin":
return <Chip label="Admin" sx={{bgcolor:"#1976d2",color:"#fff"}}/>

default:
return <Chip label={role}/>
}

}




const getStatusChip = (status) => {

switch(status){
case "active":
return <Chip label="Active" sx={{bgcolor:"#2e7d32",color:"#fff"}}/>

case "suspended":
return <Chip label="Suspended" sx={{bgcolor:"#ed6c02",color:"#fff"}}/>

default:
return <Chip label={status}/>
}
}

const adminTableColumns = [
{
key:"index",
label:"S/N",
render:(_admin,index)=>(page - 1) * rowsPerPage + index + 1,
hideOnMobile:true
},
{
key:"name",
label:"Name",
render:(admin)=>`${admin.first_name} ${admin.last_name}`,
hideOnMobile:true
},
{
key:"email",
label:"Email",
render:(admin)=>admin.email
},
{
key:"role",
label:"Role",
render:(admin)=>getRoleChip(admin.role)
},
{
key:"status",
label:"Status",
render:(admin)=>getStatusChip(admin.status)
},
{
key:"created_at",
label:"Created",
render:(admin)=>new Date(admin.created_at).toLocaleDateString()
},
{
key:"actions",
label:"Actions",
align:"center",
isActions:true,
desktopCellSx:{ minWidth: 220 },
mobileValueSx: mobileActionGroupSx,
render:(admin)=>(
<Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
{isSuperAdmin && <Button size="small" variant="contained" sx={{bgcolor:"#1976d2"}} onClick={()=>navigate(`/admin/admins/${admin.id}/profile`)}>VIEW PROFILE</Button>}
<Button
size="small"
variant="contained"
sx={{bgcolor:"#1976d2"}}
onClick={()=>navigate("/admin/admin/modify",{state:{admin}})}
>
MODIFY
</Button>

<Button
size="small"
variant="contained"
sx={{bgcolor:"#ed6c02"}}
onClick={()=>navigate("/admin/admin/suspend",{state:{admin}})}
>
{admin.status !== "suspended" ? "SUSPEND" : "UNSUSPEND"}
</Button>

<Tooltip title={!isSuperAdmin ? "Only super admin allowed" : ""}>
<span>
<Button
size="small"
variant="contained"
sx={{
bgcolor:"#d32f2f",
opacity: isSuperAdmin ? 1 : 0.4
}}
disabled={!isSuperAdmin}
onClick={()=>{
if(!isSuperAdmin) return;
navigate("/admin/admin/delete",{state:{admin}});
}}
>
DELETE
</Button>
</span>
</Tooltip>
</Stack>
)
}
]

return(

<Box sx={{ ...adminPageSx }}>
<AdminPageContainer>

<Box sx={{
display:"flex",
justifyContent:"space-between",
alignItems:"center",
flexWrap:"wrap",
gap:2,
mb:3
}}>

<Typography variant="h5" fontWeight={700} sx={{color:Colors.accent}}>
Admins Management
</Typography>

{isSuperAdmin && (
<Button
variant="contained"
sx={{
backgroundColor:Colors.accent,
color:"#003300",
fontWeight:600,
width:{ xs:"100%", sm:"auto" }
}}
onClick={()=>navigate("/admin/admin/create")}
>
+ Create Admin
</Button>
)}

</Box>




<Box sx={{ ...filterStackSx, mb:2 }}>

<TextField
placeholder="Search name or email"
value={search}
onChange={(e)=>setSearch(e.target.value)}
size="small"
fullWidth
sx={adminFieldSx}
/>

<TextField
select
value={roleFilter}
onChange={(e)=>setRoleFilter(e.target.value)}
size="small"
fullWidth
sx={adminFieldSx}
>
<MenuItem value="all">All Roles</MenuItem>
<MenuItem value="super_admin">Super Admin</MenuItem>
<MenuItem value="admin">Admin</MenuItem>
</TextField>

</Box>




<Card sx={{
p:{ xs:2, sm:2.5 },
borderRadius:3,
...contentCardSx
}}>
<Box sx={{ position:"relative", minHeight:380 }}>
<ResponsiveDataTable
columns={adminTableColumns}
rows={paginatedAdmins}
loading={loading}
minWidth={900}
emptyMinHeight={300}
emptyMessage="No admins found"
mobileTitle={(admin)=>`${admin.first_name} ${admin.last_name}`}
/>

<TableLoadingOverlay loading={loading}/>

</Box>


{!loading && filteredAdmins.length > rowsPerPage ? (
<Stack sx={paginationBarSx}>
<Typography variant="body2" sx={{ color: "text.secondary" }}>
Page {page} of {totalPages || 1}
</Typography>
<Pagination
count={totalPages || 1}
page={page}
onChange={(_event, value)=>setPage(value)}
color="primary"
shape="rounded"
showFirstButton
showLastButton
sx={{
"& .MuiPaginationItem-root": { color: "text.primary" },
"& .Mui-selected": {
bgcolor: `${Colors.accent} !important`,
color: "#003300 !important"
}
}}
/>
</Stack>
) : null}

</Card>

</AdminPageContainer>
</Box>

)

}
